import React, { useState, useEffect, useRef } from "react";

import { useDocumentDataOnce } from "react-firebase-hooks/firestore";

import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import pako from "pako";

import { useDebounce } from "use-debounce";
import { v4 as uuidv4 } from "uuid";
import * as awarenessProtocol from "y-protocols/awareness.js";
import { MathInline } from "./extensions/EZTex";

import Swal from "sweetalert2";

// tiptap
import { useEditor, EditorContent, Extension, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
import Image from "@tiptap/extension-image";
import Dropcursor from "@tiptap/extension-dropcursor";
import Placeholder from "@tiptap/extension-placeholder";

import { RunnableCode } from "./extensions/RunnableCode";
import { InlineTex } from "./extensions/InlineTex";
import { BlockTex } from "./extensions/BlockTex";

// yjs
import * as Y from "yjs";
import { WebrtcProvider } from "y-webrtc";
import { Dropdown } from "react-bootstrap";
import { firestore } from "firebaseApp.js";

import './TipTap.scss';
import { ButtonBar } from "./ButtonBar";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import { KindImage } from "./extensions/KindImage";
import { handleDrop } from "./handlers/handleDrop";

export const WEBRTC_PROVIDERS = [
  'wss://y-webrtc-eu.fly.dev'
  // 'wss://y-webrtc-signaling-eu.herokuapp.com',
];

export const fileMap = new Map();

/**
 * Props:
 * editable: boolean. Can you edit the doc?
 * firebaseDocPath: string. Where in the firebase should we write the doc? We use our own
 *    format for this doc path, so I would recommend a doc that isn't used for anything else
 * onServerWrite: function with one parameter. An optional function which is called each time
 *    we write to the server. This can be used to do something like update a search index. It
 *    will be passed a JSON containing the content of the editor. Why use the JSON? If you
 *    want to highlight a found string in the editor, you will need the JSON format. Note, if
 *    you have multiple clients connected to the same document, only one of them will be in
 *    charge up updating the database.
 *
 *
 * TipTap is the *best* editor I have found. It is built off of ProseMirror, is
 * actively supported and just works so well. It also has principled support
 * for collaborative editing and for creating custom components.
 *
 * Collaboration:
 * Collaboration is built off the amazing yjs library. yjs can be backed by either
 * webrtc either serverlessly, or with a server. The server is the recommended option
 * however it doesn't work well with firebase. For the time being I am having the clients
 * take turns being the centralized server.
 * The "truth" is the webrtc yjs object. However this truth needs to be saved to the db
 * and loaded when a new editing room is created. Who should save? who should load?
 * The great idea is to use the following protocol:
 * whomever has the lowest sessionId is in charge of backing up to the firebase.
 * whomever is first to open the document is in charge of loading from the firebase.
 *
 * Loading / saving to the database:
 * Loading: set the editor content. This will propogate to future yjs connections
 * Saving: save the json of the editor content
 *
 * Notes on YJS and firebase:
 * https://github.com/yjs/yjs/issues/189
 *
 * I thought this conversation was very helpful:
 * https://discuss.yjs.dev/t/persisting-to-db-could-it-be-this-easy/358/3
 *
 * "awareness" is an important concept! Its used for things like...
 * knowing who else is online
 * keeping track of their cursor position etc
 *
 * Known issues:
 * - Cant drag/drop code
 */

const MAX_DOC_BYTES = 1024 * 1024;       // Firestore doc limit ≈ 1 MiB
const RAW_COMPACT_TRIGGER = 850 * 1024;  // when raw > 850KB, try compact first

export const TipTap = (props) => {

  const {
    editable,
    firebaseDocPath,
  } = props

  // this component is not (by default) reactive to
  // firebaseDocPath changing, or collaborative changing
  // if either of those change, we need to force react
  // to re-create this component. We do that by giving
  // the component a unique key based on the two components


  if (!firebaseDocPath) {
    return <>No firebaseDocPath</>;
  }

  // force the component to un-mount if any of these change
  const uniqueKey = `${firebaseDocPath}-${editable}`
  return <TipTapSafe {...props} key={uniqueKey} />

}

const TipTapSafe = ({
  editable,
  firebaseDocPath,
  user,
  minHeight
}) => {

  // listen to changes to the firebase document path
  const [serverData, serverDataLoading, serverDataError] = useDocumentDataOnce(
    doc(getFirestore(), firebaseDocPath)
  );
  if (serverDataLoading) return <></>;
  if (serverDataError) return <p>Error loading data</p>;

  return (
    <TipTapWithData
      serverData={serverData}
      firebaseDocPath={firebaseDocPath}
      editable={editable}
      user={user}
      minHeight={minHeight}
    />
  );
};

/**
 * Main job is to control multiple editors
 */
export const TipTapWithData = ({
  editable,
  firebaseDocPath,
  serverData,
  user,
  minHeight
}) => {

  // test cases
  const docPathRef = useRef(firebaseDocPath)
  const editableRef = useRef(editable)
  useEffect(() => {
    if (!firebaseDocPath) {
      throw new Error("firebaseDocPath is null in TipTapSafe")
    }
    if (docPathRef.current !== firebaseDocPath) {
      throw new Error("firebaseDocPath changed in TipTapSafe")
    }
    if (editableRef.current !== editable) {
      throw new Error("editable changed in TipTapSafe")
    }
  }, [firebaseDocPath, editable])

  let [webProvider, setWebProvider] = useState(null);

  // for writing to the firebase. ready changes are the json to save
  // which gets debounced to prevent too many writes.
  const [readyChanges, setReadyChanges] = useState(null);
  const [debouncedChanges] = useDebounce(readyChanges, 500);

  const isLoggingUser = (awareness) => {
    /**
     * GOAL: I would like this to alternate answers among users to add some
     * robustness
     * Ways that I could possibly do this include:
     *  - take the time and mod it by half a second, and use this
     *    to chose which person should write (edit: wont work)
     *  - in the awareness dicitonary, we could record the last time
     *    that the person wrote to the database. Person who is supposed
     *    to write is the one who has written the least recently (with
     *    some code to manage ties and to manage users who never write)
     *  - everyone debounces every 250 * N_USERS ms and we somehow make
     *    sure that they are well stratified (edit: wont work)
     */

    // are you in charge of saving to the database?
    let states = awareness.getStates();

    let userArray = Array.from(states).map((state) => state);

    let minUserId = awareness.clientID;
    for (let currUser of userArray) {
      let userId = currUser[0];
      if (userId < minUserId) {
        minUserId = userId;
      }
    }
    return minUserId == awareness.clientID;
  };

  const onUpdate = (json, html, text) => {
    // the underlying json has changed
    if (isLoggingUser(webProvider.awareness)) {
      setReadyChanges(json);
    }
  };

  useEffect(() => {
    if (!editable) return;
    if (!debouncedChanges || !webProvider) return;

    (async () => {
      try {
        // --- 1) Encode current Y.Doc (full state)
        let raw = Y.encodeStateAsUpdateV2(webProvider.doc);
        let usedCompact = false;

        // --- 2) If raw is big, rebuild a minimal Y.Doc from the *visible* editor JSON
        if (raw.byteLength > RAW_COMPACT_TRIGGER) {
          const tmpYDoc = new Y.Doc();

          const tmpEditor = new Editor({
            extensions: [
              StarterKit.configure({ history: false }),
              InlineTex,
              BlockTex,
              MathInline,
              RunnableCode,
              Image.configure({ inline: false }),
              KindImage,
              Collaboration.configure({ document: tmpYDoc }),
            ],
            editable: false,
          });

          // Use the debounced JSON you're already persisting
          tmpEditor.commands.setContent(debouncedChanges, false);
          raw = Y.encodeStateAsUpdateV2(tmpYDoc);
          tmpEditor.destroy();
          usedCompact = true;
        }

        // --- 3) Gzip it
        const gz = pako.deflate(raw); // Uint8Array
        const version = usedCompact ? "V2+gzip+compact" : "V2+gzip";

        // Optional: last safety check (very rare to fail after gzip+compact)
        if (gz.byteLength >= MAX_DOC_BYTES) {
          // If this ever triggers, move the payload to Cloud Storage instead.
          // Example:
          // const storagePath = `ydocs/${getUniqueProviderRoom(firebaseDocPath)}/snapshot.bin`;
          // await firebase.storage().ref().child(storagePath).put(new Blob([gz]));
          // await setDoc(doc(getFirestore(), firebaseDocPath), { storagePath, version, ... }, { merge: true });
          throw new Error(
            `Compressed snapshot still ${Math.round(gz.byteLength / 1024)}KB ` +
            `>= Firestore 1MiB limit. Consider Storage fallback for this doc.`
          );
        }

        // --- 4) Save to Firestore
        const blob = firebase.firestore.Blob.fromUint8Array(gz);
        await setDoc(
          doc(getFirestore(), firebaseDocPath),
          {
            content: debouncedChanges,     // your tiptap JSON (small)
            ydoc: blob,                    // gzipped v2 bytes
            version,                       // 'V2+gzip' or 'V2+gzip+compact'
            size_raw: raw.byteLength,      // for debugging/metrics
            size_gzip: gz.byteLength,      // for debugging/metrics
            lastEdit: new Date(),
          },
          { merge: true }
        );
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to save to server",
          position: "top-end",
          text: error.message,
        });
      }
    })();
  }, [debouncedChanges, editable, webProvider, firebaseDocPath]);

  // Constructor
  useEffect(() => {
    let roomName = getUniqueProviderRoom(firebaseDocPath);
    let ydoc = new Y.Doc();

    if (serverData && serverData.ydoc) {
      // load a ydoc from the data if it has one
      // be careful: dont try and inject from the json
      // that can lead to strange sync issues with yjs
      const docBlob = serverData.ydoc;
      let bytes = docBlob.toUint8Array();

      const v = serverData.version || "V1";
      const isGzip = v.includes("+gzip");
      const isV2 = v.startsWith("V2");

      if (isGzip) {
        bytes = pako.inflate(bytes);
      }

      if (isV2) {
        Y.applyUpdateV2(ydoc, bytes);
      } else {
        Y.applyUpdate(ydoc, bytes);
      }


    }

    let awareness = new awarenessProtocol.Awareness(ydoc);
    const provider = new WebrtcProvider(roomName, ydoc, {
      signaling: WEBRTC_PROVIDERS,
      awareness,
    });

    setWebProvider(provider);

    return () => {
      provider.destroy()
    };
  }, []);

  // wait until you are ready...
  if (!webProvider) {
    return <>loading...</>;
  }

  return (
    <TipTapWithDoc
      onUpdate={onUpdate}
      provider={webProvider}
      editable={editable}
      user={user}
      minHeight={minHeight}
    />
  );
};

// for the cursors of collaborative users
const USER_COLORS = [
  "#958DF1",
  "#F98181",
  "#FBBC88",
  "#FAF594",
  "#70CFF8",
  "#94FADB",
  "#B9F18D",
];
const random_color = () => {
  const index = Math.floor(Math.random() * USER_COLORS.length);
  return USER_COLORS[index];
};

/**
 * This is the actual editor
 */
const TipTapWithDoc = ({ provider, onUpdate, editable, user, minHeight }) => {
  const editorRef = useRef<Editor | null>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // The Collaboration extension comes with its own history handling
        // (you don't undo other peoples changes)
        history: false,
      }),
      InlineTex,
      BlockTex,
      MathInline,
      RunnableCode,
      Placeholder.configure({
        placeholder: "Write something …",
      }),
      // for backwards compatibility
      Image.configure({
        inline: false,
      }),
      KindImage,
      // Register the document with Tiptap
      Collaboration.configure({
        document: provider.doc,
      }),
      // Register the collaboration cursor extension
      CollaborationCursor.configure({
        provider: provider,
        user: {
          name: user.displayName,
          color: random_color()
        },
      }),
    ],
    editorProps: {
      handleDrop: (view, event, slice, moved) => handleDrop(view, event, slice, moved),
    },
    editable: editable,
  });
  editorRef.current = editor

  const insertInlineLaTex = () => {
    editorRef.current.chain().focus().insertContent("<inline-tex></inline-tex>").run()
    return true
  }

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
      editor.chain().focus().insertContent("").run();
    }
  }, [editable]);

  useEffect(() => {
    if (!editor) return;
    editor.on("update", ({ editor }) => {
      let json = editor.getJSON();
      let html = editor.getHTML();
      let text = editor.getText();
      onUpdate(json, html, text);
    });
  }, [editor]);

  const uploadImg = (file) => {
    const storage = firebase.storage();
    const storageRef = storage.ref();
    const folder = user.uid;
    const uuid = uuidv4();
    const uploadTask = storageRef.child(folder + "/" + uuid).put(file);

    uploadTask.on(
      firebase.storage.TaskEvent.STATE_CHANGED,
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        console.log(progress, snapshot.bytesTransferred, snapshot.totalBytes);
        if (progress == 100) {
          Swal.fire({
            title: `Uploading image: Complete`,
            toast: true,
            timer: 2000,
            showConfirmButton: false,
            timerProgressBar: true,
            showClass: {
              popup: "none",
            },
          });
        } else {
          Swal.fire({
            title: `Uploading image: ${progress}%`,
            toast: true,
            timer: 10000,
            showClass: {
              popup: "none",
            },
            showConfirmButton: false,
          });
        }
      },
      (error) => {
        throw error;
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((url) => {
          insertImage(url);
        });
      }
    );
  };

  const onInsertImage = () => {
    Swal.fire({
      title: "Select image",
      input: "file",
      inputAttributes: {
        accept: "image/*",
        "aria-label": "Upload your profile picture",
      },
    }).then((e) => {
      const file = e.value;
      if (file) {
        uploadImg(file);
      }
    });
  };

  const insertImage = (imgUrl) => {
    editor.chain().focus().insertContent(`<img src="${imgUrl}"></img>`).run();
  };

  // Handle drag-and-drop
  const handleDragOver = (e) => {
    console.log("drag over");
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  return (
    <div
      className="tiptapWrapper"
      onDragOver={handleDragOver}
    >
      <ButtonBar
        editor={editor}
        editable={editable}
        onInsertImage={onInsertImage}
      />
      <div
        style={{ minHeight }}
        className={"tiptapContentWrapper " + (editable ? "editor editableWrapper" : "")}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

const getUniqueProviderRoom = (firebaseUrl) => {
  const SALT = "cs109psetapp25041989";
  return SALT + firebaseUrl.replaceAll("/", "-");
};